import { useState } from 'react';
import { supabase } from '../config/supabase';
import stripePromise from '../config/stripe';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export const useStripeCheckout = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const createCheckoutSession = async (courseId, couponCode = null) => {
    if (!user) {
      toast.error('Please sign in to enroll in courses');
      return { error: 'Not authenticated' };
    }

    setLoading(true);
    
    try {
      // Call Supabase Edge Function to create Stripe session
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          courseId,
          userId: user.id,
          couponCode,
          successUrl: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/courses/${courseId}`
        }
      });

      if (error) throw error;

      if (data.sessionId) {
        const stripe = await stripePromise;
        const { error: stripeError } = await stripe.redirectToCheckout({
          sessionId: data.sessionId
        });

        if (stripeError) {
          throw new Error(stripeError.message);
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Failed to start checkout process');
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const validateCoupon = async (couponCode, coursePrice) => {
    try {
      const { data, error } = await supabase.rpc('validate_coupon', {
        coupon_code: couponCode,
        course_price: coursePrice
      });

      if (error) throw error;

      return data[0] || { is_valid: false, discount_amount: 0, error_message: 'Invalid response' };
    } catch (error) {
      console.error('Coupon validation error:', error);
      return { is_valid: false, discount_amount: 0, error_message: error.message };
    }
  };

  return {
    createCheckoutSession,
    validateCoupon,
    loading
  };
};

export const usePaymentVerification = () => {
  const [loading, setLoading] = useState(false);

  const verifyPayment = async (sessionId) => {
    setLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('verify-payment', {
        body: { sessionId }
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Payment verification error:', error);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  return {
    verifyPayment,
    loading
  };
};